'use client';

import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import Link from 'next/link';
import './predictive.css';

/* All styling lives in predictive.css (scoped to .pi-page).
   Images go in public/assets/images/ .
   "Where predictive intelligence makes an impact" cards: har card ka image path aur link neeche `industries` array me `img` / `href` field me badlo. */

type IconName =
  | 'bars' | 'alert' | 'bulb' | 'gear' | 'cart' | 'bank' | 'bolt' | 'heart'
  | 'factory' | 'db' | 'spark' | 'cube' | 'chart' | 'shield' | 'arrow' | 'up' | 'down';

function Icon({ name }: { name: IconName }) {
  const p: Record<IconName, ReactNode> = {
    bars: <><path d="M4 20h16" /><rect x="5" y="11" width="3" height="9" /><rect x="10.5" y="5" width="3" height="15" /><rect x="16" y="9" width="3" height="11" /></>,
    alert: <><path d="M12 4 3 19h18z" /><path d="M12 10v4M12 17h.01" /></>,
    bulb: <><path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-3.5 10.9c.6.5 1 1.2 1 2.1h5c0-.9.4-1.6 1-2.1A6 6 0 0 0 12 3z" /></>,
    gear: <><circle cx="12" cy="12" r="3" /><path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1" /></>,
    cart: <><path d="M3 4h2l2.5 11h10L20 8H6.5" /><circle cx="9" cy="19" r="1.5" /><circle cx="17" cy="19" r="1.5" /></>,
    bank: <><path d="M3 9 12 4l9 5H3zM5 9v8M9.5 9v8M14.5 9v8M19 9v8M3 20h18" /></>,
    bolt: <><path d="M13 3 5 13h6l-1 8 8-10h-6z" /></>,
    heart: <><path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z" /></>,
    factory: <><path d="M3 20V9l6 3V9l6 3V5h3v15z" /><path d="M7 20v-3M12 20v-3" /></>,
    db: <><ellipse cx="12" cy="6" rx="7" ry="3" /><path d="M5 6v6c0 1.7 3 3 7 3s7-1.3 7-3V6M5 12v6c0 1.7 3 3 7 3s7-1.3 7-3v-6" /></>,
    spark: <><path d="M12 3c.6 5 3 7.4 8 9-5 1.6-7.4 4-8 9-.6-5-3-7.4-8-9 5-1.6 7.4-4 8-9z" /></>,
    cube: <><path d="M12 3 4 7.5v9L12 21l8-4.5v-9z" /><path d="m4 7.5 8 4.5 8-4.5M12 12v9" /></>,
    chart: <><path d="M4 18 9 12l4 3 7-9" /><path d="M4 4v16h16" /></>,
    shield: <><path d="M12 3 5 6v5c0 4.5 3 8 7 10 4-2 7-5.5 7-10V6z" /><path d="m9 12 2 2 4-4" /></>,
    arrow: <><path d="M5 12h14M13 6l6 6-6 6" /></>,
    up: <><path d="M12 19V5M6 11l6-6 6 6" /></>,
    down: <><path d="M12 5v14M6 13l6 6 6-6" /></>,
  };
  return (
    <svg className="pi-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {p[name]}
    </svg>
  );
}

/* ============================== DATA ============================== */

const partners = ['SAP', 'Salesforce', 'Microsoft', 'AWS', 'Google Cloud'];

const capabilities: { icon: IconName; title: string; text: string }[] = [
  { icon: 'bars', title: 'Forecast & Plan', text: 'Predict demand, sales and resource needs with greater accuracy.' },
  { icon: 'alert', title: 'Identify & Mitigate Risk', text: 'Detect potential issues before they impact your business.' },
  { icon: 'bulb', title: 'Discover Opportunities', text: 'Find new growth opportunities using data-driven insights.' },
  { icon: 'gear', title: 'Optimise Operations', text: 'Improve efficiency and reduce cost with forward-looking recommendations.' },
];

const outcomes: { value: string; label: string; dir: 'up' | 'down' }[] = [
  { value: '30%', label: 'Lower risk of disruptions', dir: 'down' },
  { value: '25%', label: 'Higher forecast accuracy', dir: 'up' },
  { value: '20%', label: 'Reduction in operational costs', dir: 'down' },
  { value: '35%', label: 'Improvement in revenue opportunities', dir: 'up' },
];

/* img = full path from public/ folder (extension ke saath). Har card ke liye alag file daalo.
   href = "See use case" link. */
const industries: { icon: IconName; name: string; text: string; img: string; href: string }[] = [
  {
    icon: 'factory',
    name: 'Manufacturing',
    text: 'Predict equipment failures and optimise maintenance.',
    img: '/assets/images/manufacturing2.jpg',
    href: '/industries/manufacturing',
  },
  {
    icon: 'cart',
    name: 'Retail & Consumer',
    text: 'Forecast demand and manage inventory effectively.',
    img: '/assets/images/Retail-&-CPG1.jpg',
    href: '/industries/fmcg-retail',
  },
  {
    icon: 'bank',
    name: 'Banking & Financial Services',
    text: 'Detect risk, prevent fraud and forecast customer behaviour.',
    img: '/assets/images/bfsi.jpg',
    href: '/industries/bfsi',
  },
  {
    icon: 'bolt',
    name: 'Energy & Utilities',
    text: 'Predict consumption, optimise supply and improve grid reliability.',
    img: '/assets/images/Utilities-&-Energy.jpg',
    href: '/industries/energy-utilities',
  },
  {
    icon: 'heart',
    name: 'Life Sciences & Healthcare',
    text: 'Anticipate patient needs and optimise resources.',
    img: '/assets/images/pharma.jpg',
    href: '/industries/pharma-lifescience',
  },
];

const approach: { icon: IconName; title: string; text: string }[] = [
  { icon: 'gear', title: 'Understand', text: 'Define business questions and outcomes.' },
  { icon: 'db', title: 'Prepare', text: 'Bring together and enrich relevant data.' },
  { icon: 'chart', title: 'Build', text: 'Develop and validate predictive models.' },
  { icon: 'spark', title: 'Operationalise', text: 'Integrate into business processes and measure impact.' },
];

const impact = [
  { value: '2x', label: 'Faster time to insight' },
  { value: '40%', label: 'More accurate forecasts' },
  { value: 'Higher', label: 'Business resilience and agility' },
];

const whyOrane: { icon: IconName; title: string; text: string }[] = [
  { icon: 'bars', title: 'Business-led approach', text: 'We focus on real business outcomes, not just models.' },
  { icon: 'cube', title: 'Deep domain expertise', text: 'Experience across industries and enterprise systems.' },
  { icon: 'gear', title: 'End-to-end delivery', text: 'From data preparation to deployment and adoption.' },
  { icon: 'shield', title: 'Responsible AI', text: 'Built with governance, transparency and compliance in mind.' },
];

/* ===================== SCROLL REVEAL ===================== */
/* One fade-up on section content. Content stays visible if JS or IntersectionObserver is missing. */
function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const root = ref.current;
    if (!root || typeof IntersectionObserver === 'undefined') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('rv--in');
            io.unobserve(e.target);
          }
        }),
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    root.querySelectorAll<HTMLElement>('.sec-head, .cap, .out, .ind-card, .step, .imp, .why').forEach((n, i) => {
      n.classList.add('rv');
      n.style.setProperty('--rv-delay', `${(i % 5) * 70}ms`);
      io.observe(n);
    });
    return () => io.disconnect();
  }, []);
  return ref;
}

/* ============================== PAGE ============================== */

export default function PredictiveIntelligence() {
  const ref = useReveal();

  return (
    <div className="pi-page" ref={ref}>
      <main>
        {/* HERO */}
        <section className="pi-hero">
          <div className="pi-hero-bg"></div>
          <div className="pi-hero-overlay"></div>
          <div className="op-container pi-hero-inner">
            <div className="pi-hero-content">
              <h1 className="pi-hero-title">
                See what&rsquo;s next and stay ahead with <span>Predictive Intelligence.</span>
              </h1>
              <p className="pi-hero-text">
                We help organisations turn data into forward-looking insights so they can anticipate
                change, reduce risk and make faster, smarter decisions.
              </p>
              <div className="pi-actions">
                <Link className="btn-primary" href="/tellusyourproblem#tell-details">Talk to Our Experts</Link>
                <Link className="btn-outline" href="#industries">Explore Use Cases</Link>
              </div>
            </div>
          </div>
        </section>

      
        {/* TURN DATA INTO WHAT'S NEXT: stacked rows */}
        <section className="op-section">
          <div className="op-container">
            <div className="turn-grid">
              <div className="sec-head turn-head">
                <h2 className="sec-title">Turn data into <span>what&rsquo;s next.</span></h2>
                <p className="sec-sub">
                  Our predictive intelligence solutions combine advanced analytics, machine learning and
                  domain expertise to help you anticipate outcomes and act early.
                </p>
                <Link className="text-link" href="/whatwedo/Ai-and-Intelligence">
                  Explore our capabilities <Icon name="arrow" />
                </Link>
              </div>
              <div className="cap-list">
                {capabilities.map((c) => (
                  <article className="cap" key={c.title}>
                    <span className="cap-icon"><Icon name={c.icon} /></span>
                    <div>
                      <h3 className="card-title">{c.title}</h3>
                      <p className="card-text">{c.text}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* REAL OUTCOMES */}
        <section className="op-section op-section--ruled">
          <div className="op-container">
            <div className="outcome-grid">
              <figure className="outcome-photo" role="img" aria-label="Analyst viewing a global forecast map on a large display">
                <figcaption>Anticipate change. Create advantage.</figcaption>
              </figure>
              <div>
                <div className="sec-head outcome-head">
                  <h2 className="sec-title">Real outcomes from predictive intelligence.</h2>
                </div>
                <div className="out-grid">
                  {outcomes.map((o) => (
                    <div className="out" key={o.label}>
                      <div className="out-value">
                        <strong>{o.value}</strong>
                        <Icon name={o.dir} />
                      </div>
                      <span>{o.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* INDUSTRIES */}
        <section className="op-section op-section--ruled" id="industries">
          <div className="op-container">
            <div className="ind-head">
              <div className="sec-head">
                <h2 className="sec-title">Where predictive intelligence makes an impact.</h2>
              </div>
              <Link className="text-link" href="/industries">
                Explore more industries <Icon name="arrow" />
              </Link>
            </div>
            <div className="ind-grid">
              {industries.map((ind) => (
                <article className="card ind-card" key={ind.name}>
                  <div className="ind-img" style={{ background: '#f4f8fd' }}>
                    <img src={ind.img} alt={ind.name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block', maxWidth: 'none' }} />
                  </div>
                  <div className="ind-body">
                    <div className="ind-title">
                      <Icon name={ind.icon} />
                      <h3 className="card-title">{ind.name}</h3>
                    </div>
                    <p className="card-text">{ind.text}</p>
                    <Link className="text-link" href={ind.href}>
                      See use case <Icon name="arrow" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* APPROACH: ruled steps with progress line */}
        <section className="op-section op-section--ruled">
          <div className="op-container">
            <div className="sec-head">
              <h2 className="sec-title">Our approach</h2>
            </div>
            <ol className="steps">
              {approach.map((a, i) => (
                <li className="step" key={a.title}>
                  <span className="step-num">{String(i + 1).padStart(2, '0')}</span>
                  <Icon name={a.icon} />
                  <h3 className="card-title">{a.title}</h3>
                  <p className="card-text">{a.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* IMPACT BAND (white) */}
        <section className="op-section op-section--ruled">
          <div className="op-container">
            <div className="imp-wrap">
              <div className="sec-head imp-head">
                <h2 className="sec-title">Make smarter decisions today for a stronger tomorrow.</h2>
                <Link className="btn-outline" href="#industries">Explore Use Cases</Link>
              </div>
              <div className="imp-grid">
                {impact.map((m) => (
                  <div className="imp" key={m.label}>
                    <strong>{m.value}</strong>
                    <span>{m.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* WHY ORANE */}
        <section className="op-section op-section--ruled">
          <div className="op-container">
            <div className="sec-head">
              <h2 className="sec-title">Why Orane for Predictive Intelligence?</h2>
            </div>
            <div className="why-grid">
              {whyOrane.map((w) => (
                <div className="why" key={w.title}>
                  <Icon name={w.icon} />
                  <div>
                    <h3 className="card-title">{w.title}</h3>
                    <p className="card-text">{w.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="pi-cta" id="contact">
          <div className="pi-cta-bg"></div>
          <div className="pi-cta-overlay"></div>
          <div className="op-container pi-cta-inner">
            <h2 className="pi-cta-title">Talk to our predictive intelligence experts.</h2>
            <p className="pi-cta-text">Tell us what you need to anticipate. We will show you how predictive intelligence can help.</p>
            <Link className="btn-primary" href="/tellusyourproblem#tell-details">Tell Us Your Problem</Link>
          </div>
        </section>
      </main>
    </div>
  );
}